import { useEffect, useMemo, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/cn"
import type {
  AgentPoolOption,
  EscalationPolicy,
  PolicyAgentOption,
  PolicyPriority,
  PolicyUserOption,
  TicketQueueOption,
} from "@/types/escalation_policy"

// Compact left-side row for the Auto Triage list+editor layout. No inline
// editor — clicking the row body fires `onSelect(policy.id)` and the right
// pane (PolicyEditorPanel) takes over. Diff chip + colored left-border
// still come from `policy.diffStatus` so the draft view reads at a glance.
//
// Grid columns:
//   24px drag-handle | 28px position | 1fr name+chips+summary | 44px active
//   toggle | 28px overflow ⋯
//
// Exported for parent (Index.tsx) to pin the list-card width if it wants.

export const POLICY_GRID_COLS =
  "grid grid-cols-[24px_28px_minmax(0,1fr)_44px_28px] items-start gap-2"

const MAX_TAG_CHIPS = 3

type Props = {
  policy: EscalationPolicy
  users: PolicyUserOption[]
  agents: PolicyAgentOption[]
  agentPools: AgentPoolOption[]
  ticketQueues: TicketQueueOption[]
  /** True in draft view — gates drag-handle, ⋯ menu, toggle. */
  editable: boolean
  /** True if this row is the one currently being edited in the right pane. */
  selected: boolean
  /** Index of this row in the FULL policies array (not the filtered view).
   *  Used as the drop-target index for native HTML5 DnD reorder. */
  fullIndex: number
  onSelect: (id: number) => void
  onDelete: (id: number) => void
  onToggleActive: (id: number, active: boolean) => void
  onDuplicate?: (id: number) => void
  onRestore?: (id: number) => void
  /** Called with (draggedId, dropIndex) when the operator drops a row.
   *  No-op when editable is false. */
  onReorder?: (fromId: number, toIndex: number) => void
}

export function PolicyListRow({
  policy,
  users,
  agents,
  agentPools,
  ticketQueues,
  editable,
  selected,
  fullIndex,
  onSelect,
  onDelete,
  onToggleActive,
  onDuplicate,
  onRestore,
  onReorder,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [dragging, setDragging] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  // Close the ⋯ menu on outside-click.
  useEffect(() => {
    if (!menuOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener("mousedown", onDocClick)
    return () => document.removeEventListener("mousedown", onDocClick)
  }, [menuOpen])

  const removed = policy.diffStatus === "removed"

  const chipMeta = useMemo(() => {
    switch (policy.diffStatus) {
      case "added":   return { label: null,      tone: "success" as const, border: "border-l-status-success" }
      case "edited":  return { label: "EDITED",  tone: "warning" as const, border: "border-l-status-warning" }
      case "removed": return { label: "REMOVED", tone: "neutral" as const, border: "border-l-app-muted/40" }
      case "moved":   return { label: "MOVED",   tone: "neutral" as const, border: "border-l-transparent" }
      default:        return null
    }
  }, [policy.diffStatus])

  const ifLines   = useMemo(() => buildIfSummary(policy).slice(0, 2), [policy])
  const thenLines = useMemo(() => buildThenSummary(policy, users, agents, agentPools, ticketQueues).slice(0, 2), [policy, users, agents, agentPools, ticketQueues])
  const isCatchAll = useMemo(() => buildIfSummary(policy).length === 0, [policy])

  const tagChips    = policy.match_appuser_tags_include.slice(0, MAX_TAG_CHIPS)
  const tagOverflow = Math.max(0, policy.match_appuser_tags_include.length - MAX_TAG_CHIPS)

  const handleRowClick = (e: React.MouseEvent) => {
    // Ignore clicks coming from controls (toggle / overflow / restore).
    const t = e.target as HTMLElement
    if (t.closest("[data-row-stop]")) return
    if (removed) return
    onSelect(policy.id)
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          if (!removed) onSelect(policy.id)
        }
      }}
      draggable={editable && !!onReorder}
      onDragStart={(e) => {
        if (!editable || !onReorder) return
        setDragging(true)
        e.dataTransfer.effectAllowed = "move"
        e.dataTransfer.setData("text/x-policy-id", String(policy.id))
      }}
      onDragEnd={() => {
        setDragging(false)
        setDragOver(false)
      }}
      onDragOver={(e) => {
        if (!editable || !onReorder) return
        e.preventDefault()
        e.dataTransfer.dropEffect = "move"
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        if (!editable || !onReorder) return
        e.preventDefault()
        setDragOver(false)
        const raw = e.dataTransfer.getData("text/x-policy-id")
        const fromId = parseInt(raw, 10)
        if (!Number.isFinite(fromId) || fromId === policy.id) return
        onReorder(fromId, fullIndex)
      }}
      className={cn(
        "border-b border-border/60 last:border-b-0 transition cursor-pointer",
        chipMeta && "border-l-4",
        chipMeta?.border,
        removed && "opacity-60 cursor-not-allowed",
        selected && "bg-brand-red/[0.04] ring-1 ring-brand-red/60 border-brand-red/40",
        !selected && "hover:bg-white/[0.02]",
        dragging && "opacity-50",
        dragOver && "ring-2 ring-brand-red/60 ring-inset",
      )}
    >
      <div className={cn(POLICY_GRID_COLS, "px-3 py-3")}>
        {/* Drag handle */}
        <div
          data-row-stop
          className={cn(
            "select-none pt-1 text-app-muted",
            editable ? "cursor-grab active:cursor-grabbing" : "cursor-not-allowed opacity-30",
          )}
          aria-hidden
          title={editable ? "Drag to reorder" : "Reorder available in draft view"}
        >
          ⋮⋮
        </div>

        {/* Position */}
        <div className="pt-0.5">
          <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-control border border-border bg-white/5 px-1 text-[11px] font-semibold tabular-nums text-app-text">
            {policy.position}
          </span>
        </div>

        {/* Name + chips + summary */}
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className={cn(
                "truncate text-sm font-medium text-app-text",
                removed && "line-through",
              )}
            >
              {policy.name}
            </span>
            {chipMeta?.label && <Badge tone={chipMeta.tone}>{chipMeta.label}</Badge>}
            {policy.priority_override && (
              <Badge tone={priorityTone(policy.priority_override)}>
                {policy.priority_override}
              </Badge>
            )}
            {!policy.active && <Badge tone="neutral">archived</Badge>}
          </div>

          {tagChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {tagChips.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center rounded-chip border border-border bg-app-panel2 px-1.5 py-0.5 text-[10px] text-app-text"
                >
                  {t}
                </span>
              ))}
              {tagOverflow > 0 && (
                <span className="text-[10px] text-app-muted">+{tagOverflow} more</span>
              )}
            </div>
          )}

          <div className="text-[11px] text-app-muted">
            {isCatchAll ? (
              <div className="italic">Catch-all (matches every escalation)</div>
            ) : (
              <div className="truncate">{ifLines.join(", ")}</div>
            )}
            <div className="truncate">{thenLines.join(", ")}</div>
          </div>
        </div>

        {/* Active toggle */}
        <div data-row-stop className="pt-1">
          <ToggleSwitch
            checked={policy.active}
            disabled={!editable || removed}
            onChange={(next) => onToggleActive(policy.id, next)}
          />
        </div>

        {/* Overflow / restore */}
        <div data-row-stop className="relative pt-0.5" ref={menuRef}>
          {removed ? (
            onRestore && (
              <Button size="sm" variant="ghost" onClick={() => onRestore(policy.id)}>
                Restore
              </Button>
            )
          ) : (
            <>
              <button
                type="button"
                disabled={!editable}
                onClick={() => setMenuOpen((v) => !v)}
                className={cn(
                  "rounded-control px-1.5 py-1 text-app-muted hover:bg-white/5 hover:text-app-text",
                  !editable && "opacity-30 hover:bg-transparent",
                )}
                aria-label="Row actions"
                title="Row actions"
              >
                ⋯
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-8 z-20 w-36 rounded-control border border-border bg-app-panel2 p-1 text-sm shadow-card">
                  <MenuItem onClick={() => { setMenuOpen(false); onSelect(policy.id) }}>Edit</MenuItem>
                  {onDuplicate && (
                    <MenuItem onClick={() => { setMenuOpen(false); onDuplicate(policy.id) }}>Duplicate</MenuItem>
                  )}
                  <MenuItem
                    danger
                    onClick={() => {
                      setMenuOpen(false)
                      if (confirm(`Delete "${policy.name}"?`)) onDelete(policy.id)
                    }}
                  >
                    Delete
                  </MenuItem>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// -- helpers (re-exported so other surfaces can reuse) --------------------

export function priorityTone(p: PolicyPriority): "error" | "warning" | "neutral" | "success" {
  switch (p) {
    case "urgent": return "error"
    case "high":   return "warning"
    case "normal": return "neutral"
    case "low":    return "success"
  }
}

export function buildIfSummary(p: EscalationPolicy): string[] {
  const lines: string[] = []
  if (p.match_appuser_tags_include.length) lines.push(`Tags: ${p.match_appuser_tags_include.join(", ")}`)
  if (p.match_appuser_tags_exclude.length) lines.push(`Exclude tags: ${p.match_appuser_tags_exclude.join(", ")}`)
  if (p.match_reason_keys_include.length)  lines.push(`Reasons: ${p.match_reason_keys_include.join(", ")}`)
  if (p.match_reason_keys_exclude.length)  lines.push(`Exclude reasons: ${p.match_reason_keys_exclude.join(", ")}`)
  if (p.match_topic_keys_include.length)   lines.push(`Topics: ${p.match_topic_keys_include.join(", ")}`)
  if (p.match_topic_keys_exclude.length)   lines.push(`Exclude topics: ${p.match_topic_keys_exclude.join(", ")}`)
  if (p.match_min_recent_escalations) {
    const days = p.match_recent_window_days ?? 7
    lines.push(`≥ ${p.match_min_recent_escalations} escalations in last ${days}d`)
  }
  return lines
}

export function buildThenSummary(
  p: EscalationPolicy,
  users: PolicyUserOption[],
  agents: PolicyAgentOption[],
  agentPools: AgentPoolOption[],
  ticketQueues: TicketQueueOption[],
): string[] {
  const lines: string[] = []
  switch (p.assignee_strategy) {
    case "unassigned":
      lines.push("Leave unassigned")
      break
    case "specific_user": {
      const u = users.find((x) => x.id === p.assignee_user_id)
      lines.push(`Assign to ${u ? u.name : "—"}`)
      break
    }
    case "random_from_pool":
    case "round_robin_from_pool": {
      const pool = agentPools.find((x) => x.id === p.assignee_agent_pool_id)
      const label = p.assignee_strategy === "random_from_pool" ? "Random pool" : "Round robin"
      lines.push(`${label}: ${pool ? pool.name : "—"}`)
      if (p.prefer_topic_affinity) lines.push("Prefer topic affinity")
      break
    }
    case "route_to_agent": {
      const a = agents.find((x) => x.id === p.reroute_agent_id)
      lines.push(`Reroute to AI agent: ${a ? a.name : "—"}`)
      break
    }
    case "send_to_queue": {
      const q = ticketQueues.find((x) => x.id === p.assignee_ticket_queue_id)
      lines.push(`Send to queue: ${q ? q.name : "—"}`)
      break
    }
  }
  if (p.priority_override) lines.push(`Priority → ${p.priority_override}`)
  const slaParts: string[] = []
  if (p.sla_first_response_minutes != null) slaParts.push(`first ${p.sla_first_response_minutes}m`)
  if (p.sla_idle_response_minutes  != null) slaParts.push(`idle ${p.sla_idle_response_minutes}m`)
  if (p.sla_resolution_minutes     != null) slaParts.push(`resolve ${p.sla_resolution_minutes}m`)
  if (slaParts.length) lines.push(`SLA: ${slaParts.join(", ")}`)
  return lines
}

// -- subcomponents --------------------------------------------------------

function MenuItem({
  children, onClick, danger,
}: { children: React.ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "block w-full rounded-control px-2 py-1.5 text-left transition",
        danger ? "text-status-error hover:bg-status-error/10" : "text-app-text hover:bg-white/5",
      )}
    >
      {children}
    </button>
  )
}

function ToggleSwitch({
  checked, disabled, onChange,
}: { checked: boolean; disabled?: boolean; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition",
        checked ? "bg-brand-red" : "bg-white/10",
        disabled && "opacity-40 cursor-not-allowed",
      )}
    >
      <span
        className={cn(
          "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition",
          checked ? "translate-x-5" : "translate-x-1",
        )}
      />
    </button>
  )
}
